<template>
  <div class="tile sm-12">
    <div class="header">
      <h1 class="text-lg title">Family editing</h1>
    </div>
    <WarningDialogComponent
      title="Switch family without saving?"
      confirmButtonText="Switch"
      ref="warningDialog"
      @canceled="cancelFamilyChange"
      @confirmed="confirmFamilyChange">
      <p>All changes will be lost.</p>
      <p>Are you sure you want to switch the Family without saving?</p>
    </WarningDialogComponent>
    <TabComponent
      ref="tabsComponent"
      :tabItems="familiesTabs"
      @changeValue="tryChangeSelectedFamily"
    />
    <div class="row preview-panel">
      <ListBuilderComponent
        availableItemsTitle="Available products"
        assignedItemsTitle="Assigned products"
        selectAllLabel="Select all products"
        ref="listBuilder"
        :listItems="listItems"
        @itemsAssigned="addToPreviewBox"
        @itemsUnassigned="removeFromPreviewBox"
      />
      <FamilyPreviewEditor
        class="family-preview-box"
        ref="familyPreviewEditor"
        @savePreviewData="savePreviewData"
        :clusters="previewClusters"
        :selectedFamily="selectedFamily"
      />
      <div class="buttons-wrapper">
        <button
          class="btn"
          @click="showConfirmation">
          Publish
        </button>
      </div>
    </div>
    <ConfirmationComponent 
      :isOpen="isOpen"
      confirmName="Publish"
      @confirm="publish">
      <div slot="header">Publish</div>
      <div slot="content">Are you sure you want to publish?</div>
    </ConfirmationComponent>
  </div>
</template>

<script>
import FamiliesService from '@/services/familiesService';
import ProductService from '@/services/productService';
import ContainerComponent from '@/components/ContainerComponent';
import TabComponent from '@/components/base.component/TabComponent.vue';
import ListBuilderComponent from '@/components/ListBuilderComponent.vue';
import FamilyPreviewEditor from '@/components/FamilyPreviewEditor.vue';
import readIsManaVersionFlag from '@/shared/manaVersionHelper';
import WarningDialogComponent from '@/components/complex.component/WarningDialogComponent.vue'
import ConfirmationComponent from '@/components/base.component/ConfirmationComponent.vue';
import FamilyPreviewApiService from '@/services/familyPreviewServices/familyPreviewApiService';
import FamilyPreviewService from '@/services/familyPreviewServices/familyPreviewService';

export default {
  components: {
    ContainerComponent,
    TabComponent,
    ListBuilderComponent,
    FamilyPreviewEditor,
    WarningDialogComponent,
    ConfirmationComponent
  },
  data() {
    return {
      selectedFamily: undefined,
      selectedFamily: 'Antenna System',
      selectedFamilyProducts: [],
      familiesTabs: [],
      products: [],
      previewClusters: [],
      listItems: [],
      isMana: readIsManaVersionFlag(),
      assignedProductsCount: 0,
      isOpen: false,
      isSaved: false,
      previewState: {}
    };
  },
  async created() {
    const familiesNames = await FamiliesService.getNames(this.isMana);

    this.familiesTabs = familiesNames.map((name, index) => {
      return {
        name: name,
        isActive: index == 0
      };
    });

    if (familiesNames.length > 0) {
      this.selectedFamily = familiesNames[0];
    }

    await this.loadProducts();
    this.updatePreviewClusters();
  },

  methods: {
    async tryChangeSelectedFamily(selectedFamilyName) {
      if (this.assignedProductsCount > 0) {
        this.$refs.warningDialog.show(selectedFamilyName);
        return;
      }

      await this.changeSelectedFamily(selectedFamilyName);
    },
    async changeSelectedFamily(familyName) {
      this.products = [];
      this.selectedFamily = familyName;

      this.$refs.listBuilder.clear();
      this.$refs.familyPreviewEditor.clear();

      this.assignedProductsCount = 0;

      await this.loadProducts();
      this.updatePreviewClusters();
    },
    cancelFamilyChange() {
      this.$refs.warningDialog.hide();
      this.$refs.tabsComponent.setActive(this.selectedFamily);
    },
    async confirmFamilyChange(familyName) {
      await this.changeSelectedFamily(familyName);
      this.$refs.warningDialog.hide();
    },
    addToPreviewBox(listItems) {
      if (!this.isSaved) {
        const products = listItems
        .map(listItem => listItem.item)
        .map(this.toPreviewBoxProduct);

        this.$refs.familyPreviewEditor.addProducts(products);
      }

      this.assignedProductsCount += listItems.length;
    },
    removeFromPreviewBox(listItems) {
      const keys = listItems.map(listItem => listItem.item.productId);
      this.$refs.familyPreviewEditor.removeProducts(keys);

      this.assignedProductsCount -= listItems.length;
    },
    loadProducts() {
      const savedProduct = FamilyPreviewService.getFamilyPreview(this.selectedFamily);
      ProductService.getByFamilyName(this.selectedFamily, this.isMana).then(data => {
        if(savedProduct?.familyProducts?.length > 0) {
          this.isSaved = true;
          data.forEach(prod => {
            savedProduct.familyProducts.forEach(item => {
              if (prod.productId === item.productId) {
                this.$refs.listBuilder.selectedAvailableItems.push(this.toListItem(prod));
                this.$refs.listBuilder.assignSelectedItems();
              } else {
                const existProd = this.products.find(p => p.productId === prod.productId);
                if (existProd === undefined) {
                  const existSavedProd = savedProduct.familyProducts.find(p => p.productId === prod.productId);
                  if (existSavedProd === undefined) {
                    this.products.push(prod);
                  }
                }
              }
            })
          })
        } else {
          this.products = data;

          this.updatePreviewClusters();
        }

        this.isSaved = false;
      
        this.listItems = this.products
          .filter(product => product.parentId === null)
          .map(this.toListItem);
      });
    },
    updatePreviewClusters() {
      const productsClusters = this.products.map(product => product.cluster);
      const clustersSet = new Set(productsClusters);
      this.previewClusters = Array.from(clustersSet).map(this.toPreviewCluster);
    },
    toListItem(product) {
      return {
        label: product.name,
        key: product.productId,
        item: product,
        isDisplayed: true
      };
    },
    toPreviewCluster(cluster) {
      return {
        label: cluster
      };
    },
    toPreviewBoxProduct(product) {
      return {
        productId: product.productId,
        label: product.name,
        imageUrl: this.getImageUrl(product),
        orthoPath: product?.orthoPath
      };
    },
    getImageUrl(product) {
      if (!!product.landscapeImagePath) {
        return product.landscapeImagePath;
      }

      if (!!product.parentId) {
        const parent = this.products.find(p => p.productId === product.parentId);
        return parent?.landscapeImagePath;
      }

      return null;
    },
    async publish() {
      this.previewState = {
        ...this.$refs.familyPreviewEditor.getState(),
        isMana: this.isMana,
        familyNameId: this.selectedFamily
      };
    },

    showConfirmation() {
      this.isOpen = !this.isOpen;
    },

    async savePreviewData(dataFile) {
      await FamilyPreviewApiService.createFamilyPreview({ 
        model: this.previewState,
        fileName: this.previewState.fileName,
        imageData: dataFile });
    }
  }
};
</script>

<style lang="less" scoped>
@import '~@eds/vanilla/variables/dark.less';
@import '~@eds/vanilla/variables/global.less';

.view-container {
  overflow: scroll;
}

.tile {
  flex: 1;
  position: relative;
  background-color: @gray-09;
  height: 100% !important;
  overflow: scroll;
}

.header {
  padding: @space-base @space-large;

  .title {
    font-weight: 700;
    margin: 0;
  }
}

.preview-panel {
  background-color: @gray-14;
  padding: @space-large;
  flex: 1 0 auto;
  flex-direction: column;
  margin-top: 0.5rem;

  .list-builder {
    width: fit-content;
    margin-top: 0.5rem;
    max-height: 360px;
  }

  .family-preview-box {
    border: 1px solid @gray-46;
    margin-top: @space-xxl;
    width: 100%;
    height: 630px;
  }

  @media screen and (min-width: 1280px) {
    .family-preview-box {
      height: 760px;
    }
  }

  @media screen and (min-width: 1366px) {
    .family-preview-box {
      height: 960px;
    }
  }

  @media screen and (min-width: 2000px) {
    .family-preview-box {
      height: 960px;
    }
  }

  @media screen and (min-width: 2500px) {
    .family-preview-box {
      height: 1460px;
    }
  }

  @media screen and (min-width: 3000px) {
    .family-preview-box {
      height: 1860px;
    }
  }

  @media screen and (min-width: 4000px) {
    .family-preview-box {
      height: 2860px;
    }
  }

  .buttons-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: @space-xxl;
    z-index: 1;
  }
}
</style>
@/services/clustersService